import {
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { Store } from '@ngrx/store';
import { debounceTime } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { AttrDataScope, SourcesActions, sourcesSelectors } from '@core/tags-and-sources-data';
import type { AttributesLoadMore, SelectOption } from '@core/user-interface-legacy';
import {
  DropdownItemComponent,
  SearchFeatureDirective,
  Select2Component,
  Select2TriggerComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-candidate-sources-select-wrapper',
  templateUrl: './candidate-sources-select-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    Select2Component,
    SearchFeatureDirective,
    Select2TriggerComponent,
    DropdownItemComponent,
    TranslatePipe,
  ],
})
export class CandidateSourcesSelectWrapperComponent {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public readonly LOAD_MORE_WHEN_LEFT = 5;
  public readonly limit = 100;

  public control = input.required<FormControl<string>>();
  public isInvalid = input<boolean>(false);
  public isReadOnly = input<boolean>(false);
  public preselectedOptions = input<SelectOption[]>([]);

  public selectedOptions = output<SelectOption[]>();

  public searchControl = new FormControl('', { nonNullable: true });
  public options = signal<SelectOption[]>([]);
  public page = signal<number>(1);
  public query = signal<string>('');
  public isLoading = this.store.selectSignal(
    sourcesSelectors.isPending({
      scope: AttrDataScope.Typeahead,
    }),
  );
  public selectedItems = signal<SelectOption[]>([]);

  public ngOnInit(): void {
    this.initialLoad();
    this.selectedItems.set(this.preselectedOptions() || []);

    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(query => {
        this.onSearch(query);
      });
    const paginatedSources$ = this.store.select(
      sourcesSelectors.paginatedAttributeSelector({
        scope: AttrDataScope.Typeahead,
      }),
    );

    paginatedSources$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paginatedSources => {
      if (paginatedSources.meta) {
        this.page.set(paginatedSources.meta.page!);
      }

      this.options.set(
        paginatedSources.rows.map(attr => ({
          label: attr.value,
          value: attr.searchableName,
        })),
      );
    });

    this.control()
      .valueChanges.pipe(startWith(this.control().value), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const selectedOption = [...this.preselectedOptions(), ...this.options()].find(
          option => option.value === value,
        );
        this.selectedOptions.emit(selectedOption ? [selectedOption] : []);
      });
  }

  public loadMoreTags(params: AttributesLoadMore): void {
    if (this.isLoading()) {
      return;
    }
    this.store.dispatch(
      SourcesActions.fetchPaginatedCollection({
        payload: { params, scope: AttrDataScope.Typeahead, add: true },
      }),
    );
  }

  public onScrollChange(event: IPageInfo) {
    const isCloseToScrollEnd =
      event.endIndexWithBuffer > this.options().length - this.LOAD_MORE_WHEN_LEFT;
    const canLoadMore = this.page() * this.limit === this.options().length;
    if (isCloseToScrollEnd && canLoadMore) {
      this.loadMoreTags({
        page: this.page() + 1,
        limit: this.limit,
        query: this.query(),
      });
    }
  }

  public onSelectChange(selected: SelectOption[]): void {
    this.selectedItems.set(selected);
  }

  public onSearch(query: string): void {
    this.query.set(query);

    const params = {
      query,
      page: 1,
      limit: 100,
    };

    this.store.dispatch(
      SourcesActions.fetchPaginatedCollection({
        payload: {
          params,
          scope: AttrDataScope.Typeahead,
        },
      }),
    );
  }

  private initialLoad(): void {
    this.store.dispatch(
      SourcesActions.fetchPaginatedCollection({
        payload: {
          params: { page: this.page(), limit: this.limit },
          scope: AttrDataScope.Typeahead,
          add: true,
        },
      }),
    );
  }
}
